import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const TERMS_VERSION = '1.0.0';

export default function TermsOfService() {
  const navigate = useNavigate();

  const handleBack = () => {
    const hasSpaHistory = !!(window.history?.state && (window.history.state as any).idx > 0);
    if (hasSpaHistory) {
      navigate(-1);
    } else {
      navigate('/login');
    }
  };

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleBack();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  return (
    <div className="min-h-[100dvh] bg-white">
      <div className="sticky top-0 z-10 border-b bg-white/80 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 active:scale-[0.99] transition-all"
            aria-label="חזרה"
          >
            <ArrowRight className="h-5 w-5" />
            חזרה
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-center">תנאי שימוש – LoyalFlow</h1>
        <p className="text-sm text-gray-500 mb-8 text-center">
          עודכן: {new Date().toLocaleDateString('he-IL')} | גרסה: {TERMS_VERSION}
        </p>

        <div className="space-y-6 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">1. כללי</h2>
            <p>
              ברוכים הבאים ל-LoyalFlow. השימוש במערכת מהווה הסכמה לתנאי שימוש אלה. אנא קראו אותם בעיון.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">2. השירות</h2>
            <p>
              LoyalFlow מספקת פלטפורמה לניהול עסקים, תורים, לקוחות ומערכות נאמנות. השירות ניתן "כמות שהוא" (AS-IS).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">3. חשבון משתמש</h2>
            <ul className="list-disc mr-6 space-y-2">
              <li>אתם אחראים לשמירה על סודיות פרטי החשבון שלכם</li>
              <li>אתם אחראים לכל פעילות המתבצעת בחשבונכם</li>
              <li>אסור להעביר את החשבון לצד שלישי ללא אישור בכתב</li>
              <li>יש לדווח מיד על כל שימוש לא מורשה בחשבון</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">4. שימוש מותר</h2>
            <p className="mb-2">השימוש במערכת מותר אך ורק למטרות חוקיות. אסור:</p>
            <ul className="list-disc mr-6 space-y-2">
              <li>להשתמש במערכת באופן שעלול לפגוע או להפריע לפעילותה</li>
              <li>לנסות לגשת למידע של משתמשים אחרים ללא הרשאה</li>
              <li>להעלות תוכן זדוני, פוגעני או בלתי חוקי</li>
              <li>לעשות שימוש במערכת לשליחת ספאם או הודעות שיווקיות לא רצויות</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">5. תשלומים ומנויים</h2>
            <ul className="list-disc mr-6 space-y-2">
              <li>המחירים מפורסמים באתר ועשויים להשתנות</li>
              <li>התשלומים מתבצעים באמצעות Stripe</li>
              <li>ניתן לבטל מנוי בכל עת דרך לוח הבקרה</li>
              <li>אין החזרים על תקופות שכבר שולמו</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">6. קניין רוחני</h2>
            <p>
              כל הזכויות במערכת, בעיצוב, בקוד ובתוכן שייכות ל-LoyalFlow. אסור להעתיק, לשכפל או להפיץ ללא אישור בכתב.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">7. הגבלת אחריות</h2>
            <p>
              LoyalFlow לא תהיה אחראית לנזקים ישירים או עקיפים הנובעים משימוש או אי-שימוש במערכת,
              לרבות אובדן נתונים, הפסד רווחים או נזקים אחרים.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">8. ביטול חשבון</h2>
            <p>
              אנו שומרים לעצמנו את הזכות להשעות או לבטל חשבון במקרה של הפרת תנאי השימוש,
              ללא התראה מוקדמת ומבלי להחזיר תשלומים.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">9. שינויים בתנאי השימוש</h2>
            <p>
              אנו רשאים לעדכן את תנאי השימוש מעת לעת. שינויים מהותיים יפורסמו באתר,
              והמשך השימוש במערכת לאחר השינוי מהווה הסכמה לתנאים החדשים.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">10. דין וסמכות שיפוט</h2>
            <p>
              תנאי שימוש אלה יפורשו על-פי חוקי מדינת ישראל.
              סמכות השיפוט הבלעדית תהיה לבתי המשפט המוסמכים בישראל.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">11. יצירת קשר</h2>
            <p>
              לשאלות או הבהרות בנוגע לתנאי השימוש, ניתן לפנות אלינו בכתובת:{' '}
              <a href="mailto:support@loyalflow.co.il" className="text-indigo-600 hover:underline">
                support@loyalflow.co.il
              </a>
            </p>
          </section>

          <section className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-600">
              בשימוש במערכת LoyalFlow, אתם מאשרים שקראתם והבנתם את תנאי השימוש ואת{' '}
              <a href="/privacy-policy" className="text-indigo-600 hover:underline">
                מדיניות הפרטיות
              </a>
              , ומתחייבים לפעול על-פיהם.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
